import { createResource, Show, type JSX } from 'solid-js';
import { supabase } from '@lib/supabase/client';
import { isPrivateImageRef, privateRefToPath } from '@lib/imageUpload';

// Drop-in <img> replacement for sources that may resolve to either a
// public URL or our private-bucket reference scheme (`private:<path>`).
//
// For public/external `src` values this component renders a plain
// <img> with no extra round-trips — the only cost in that case is the
// component shell itself.
//
// For `private:<path>` references we ask the Supabase client for a
// short-lived signed URL (1 hour TTL) at render time and swap that in
// as the actual src. The reference stored in markdown text never
// expires, so a forum post written today still resolves a year later;
// each viewer just gets a fresh URL on demand.
//
// Why createResource and not createSignal?
//   * createResource integrates with Suspense, which the markdown
//     surfaces (forum bodies, blog posts) already wrap in a Suspense
//     boundary — we get a unified loading affordance for free.
//   * The `source` accessor reactively re-fetches if `props.src`
//     changes (e.g. an admin pasted a different reference into a
//     post they're editing).

type Props = {
  src: string;
  alt?: string;
  class?: string;
  /** Optional caption for screen readers / future caption strip below the image. */
  caption?: string;
};

const SIGNED_URL_TTL_SECONDS = 60 * 60;

export default function SecureImage(props: Props): JSX.Element {
  const [resolved] = createResource(
    () => props.src,
    async (src): Promise<string> => {
      if (!isPrivateImageRef(src)) return src;
      const path = privateRefToPath(src);
      const { data, error } = await supabase.storage
        .from('private_images')
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
      if (error || !data?.signedUrl) return '';
      return data.signedUrl;
    },
  );

  return (
    <Show
      when={resolved() !== undefined && resolved() !== ''}
      fallback={
        <Show
          when={resolved() === ''}
          fallback={
            // In-flight: a thin neutral block so the layout doesn't
            // jump when the signed URL lands. Inline rather than
            // <img loading="lazy"> with no src because some browsers
            // log noisy "image with no src" warnings.
            <span
              class={`inline-block bg-brand-hex/40 ${props.class ?? ''}`}
              aria-busy="true"
              aria-label={props.alt ?? 'Loading image'}
              style={{ 'min-width': '120px', 'min-height': '80px' }}
            />
          }
        >
          {/* Resolved to empty string: signing failed (signed-out user
              hitting a private image, or the object was deleted). Show
              a clear placeholder rather than a silent broken-image
              icon — patients deserve to know an image existed. */}
          <span
            class={`inline-flex items-center justify-center rounded-md bg-brand-hex/40 px-3 py-2 text-xs text-brand-green/70 ${props.class ?? ''}`}
            role="img"
            aria-label={props.alt ?? 'Image unavailable'}
          >
            Image unavailable
          </span>
        </Show>
      }
    >
      <img
        src={resolved()}
        alt={props.alt ?? ''}
        class={props.class}
        loading="lazy"
      />
    </Show>
  );
}
